// storybook-check-ignore
import React, { useState } from 'react';

import { css } from '@emotion/react';
import { Box } from '@opendoor/bricks/core';
import { AnimatePresence, motion } from 'framer-motion';

import type { MenuConfig, NavigationProps } from '../../types';
import { LanguageToggle } from '../LanguageToggle';
import { boxVariants, navList } from '../menuAnimations';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import { MenuCTA } from './MenuCTA';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: any) => void;
  displayLanguageToggle?: boolean;
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  displayLanguageToggle,
  isMegaNavEnabled,
  megaNavData,
}) => {
  const animate = isVisible ? 'visible' : 'hidden';
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const tabletDisplay = mainNavLinks.length > 2 ? 'flex' : 'none';

  return (
    <Box
      onAnimationComplete={onAnimationComplete}
      as={motion.div}
      variants={boxVariants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      exit="hidden"
      tabIndex={-1}
      ref={containerRef}
      backgroundColor="white0"
      gridArea="1 / 1 / -1 / -1"
      pt={[200, 168, 168]}
      pl={[7, null, 10]}
      pr={[7, null, 10]}
      pb={[7, null, 10]}
      maxW={['100%', null, '424px']}
      bottom="0px"
      left={[0, null, 'auto']}
      right="0"
      position="fixed"
      top="0px"
      w="100%"
      zIndex="10"
      role="dialog"
      aria-labelledby="dialog-main-menu"
      aria-modal="true"
      display="grid"
      gridTemplateRows="1fr auto auto"
      gridTemplateColumns="1fr"
      overflow="auto"
      outline="0"
      css={css`
        overscroll-behavior-y: contain;
      `}
    >
      <Box
        as={motion.ul}
        animate={animate}
        variants={navList}
        gridColumn="1"
        gridRow="1"
        m="0"
        p="0"
        role="list"
        css={css`
          list-style: none;
          position: relative;
          overflow: hidden;
        `}
      >
        <AnimatePresence initial={false}>
          {isMegaNavEnabled && megaNavData && openSubMenu ? (
            <MobileMenuSubMenu
              key="submenu"
              megaNavData={megaNavData}
              openSubMenu={openSubMenu}
              navList={navList}
              onBack={() => setOpenSubMenu(null)}
            />
          ) : (
            <MobileMenuList
              key="mainmenu"
              megaNavData={isMegaNavEnabled ? megaNavData : undefined}
              menuPanelNavLinks={menuPanelNavLinks}
              mainNavLinks={mainNavLinks}
              slug={slug}
              tabletDisplay={tabletDisplay}
              navList={navList}
              onSubMenuOpen={(itemName) => setOpenSubMenu(itemName)}
            />
          )}
        </AnimatePresence>
      </Box>
      {displayLanguageToggle && (
        <Box gridColumn="1" gridRow="2" alignSelf="end" display="flex" justifyContent="flex-start">
          <LanguageToggle />
        </Box>
      )}
      <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} animate={animate} />
    </Box>
  );
};
